import { motion } from 'framer-motion';
import { PERK_LAYOUT_IDS } from './constants';
import { PerkBanner } from './PerkBanner';
import { PerkProgressButton } from './PerkProgressButton';
import { PerkValidity } from './PerkValidity';
import { Perk } from './types';
import { formatValidityDate, isUpcoming } from './utils';
import { isNil, isEmpty } from 'lib/javascript';
import classNames from 'classnames';

export const PerkCard = ({
  perk,
  layoutIds,
  onOpen,
  onUnlock,
  onRedeemClick,
  minimizeCards,
  isApplicable,
  onApplyCoupon,
  appliedCouponCode,
}: {
  perk: Perk;
  layoutIds: typeof PERK_LAYOUT_IDS;
  onOpen: (perk: Perk) => void;
  onUnlock?: (perk: Perk) => Promise<void>;
  onRedeemClick?: (perk: Perk) => void;
  minimizeCards?: boolean;
  isApplicable?: boolean;
  onApplyCoupon?: (perk: Perk, couponCode?: string) => Promise<void>;
  appliedCouponCode?: string | null;
}) => {
  return (
    <motion.div
      layoutId={`${layoutIds.card}-${perk.id}`}
      className={`tw-w-full tw-overflow-hidden tw-relative tw-transition-shadow tw-bg-white hover:tw-shadow-[0_5px_20px_rgba(0,0,0,.05)] tw-flex tw-flex-col ${
        isApplicable &&
        appliedCouponCode &&
        perk.progress?.unlocked_reward?.reward.code ===
          appliedCouponCode
          ? 'tw-border tw-border-primary tw-rounded-lg'
          : ''
      }`}
      data-perk-id={perk.id}
    >
      <div className="tw-relative">
        <motion.div layoutId={`${layoutIds.cardImage}-${perk.id}`}>
          <PerkBanner
            banners={perk.banners}
            logo={perk.logo}
            onBannerClick={() => onOpen(perk)}
          />
        </motion.div>
        <motion.div
          className="tw-absolute tw-right-6 tw-top-6 tw-flex tw-items-center tw-cursor-pointer"
          title="View Details"
          layoutId={`${layoutIds.cardCta}-${perk.id}`}
        >
          <div
            className="tw-flex tw-items-center tw-justify-center tw-text-white tw-text-4xl tw-shadow tw-rounded-full"
            role="button"
            onClick={() => onOpen(perk)}
            title="View Perk Details"
          >
            <i className="fa fa-info-circle" />
          </div>
        </motion.div>
      </div>
      <div
        className={classNames(
          'tw-relative tw-bg-white tw-p-4 tw-flex tw-flex-col tw-flex-1',
          minimizeCards ? 'tw-gap-y-5 tw-mt-1' : 'tw-gap-y-3',
        )}
      >
        {!minimizeCards && (
          <motion.div
            layoutId={`${layoutIds.cardValidity}-${perk.id}`}
          >
            <PerkValidity
              startedAt={formatValidityDate(perk.started_at)}
              endedAt={formatValidityDate(perk.ended_at)}
              quota={perk.quota}
              totalRedeemed={perk.total_redeem}
              isUpcoming={isUpcoming(perk.started_at)}
            />
          </motion.div>
        )}
        <motion.div
          layoutId={`${layoutIds.cardTitle}-${perk.id}`}
          className="tw-flex tw-flex-col tw-gap-2"
        >
          <h2 className="tw-text-center tw-font-extrabold tw-text-3xl tw-mb-0">
            {perk.title}
          </h2>
          {!minimizeCards && (
            <p
              className="tw-mb-0 tw-leading-tight tw-text-2xl tw-text-center tw-text-[#333] lg:tw-min-h-[4rem] tw-overflow-hidden"
              style={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
              }}
            >
              {perk.excerpt}
            </p>
          )}
        </motion.div>
        {(isUpcoming(perk.started_at) || !perk.progress) &&
          perk.partner_name && (
            <div className="tw-border-t tw-h-0 tw-mt-2.5 tw-mb-1" />
          )}
        {!isUpcoming(perk.started_at) && perk.progress && (
          <motion.div
            layoutId={`${layoutIds.cardProgress}-${perk.id}`}
            className="tw-flex tw-flex-col tw-h-full tw-justify-end"
          >
            <PerkProgressButton
              perk={perk}
              onUnlock={onUnlock}
              onRedeemClick={onRedeemClick}
              isApplicable={isApplicable}
              onApplyCoupon={onApplyCoupon}
              appliedCouponCode={appliedCouponCode}
            />
          </motion.div>
        )}
        {!minimizeCards && (
          <motion.div
            layoutId={`${layoutIds.cardPartner}-${perk.id}`}
            className="tw-flex tw-items-center tw-justify-center"
          >
            {!isNil(perk.partner_name) &&
            !isEmpty(perk.partner_name) ? (
              <span className="tw-text-[1.375rem] tw-text-white-[#777]">
                Courtesy of{' '}
                <strong>
                  {!isNil(perk.partner_name) &&
                  !isEmpty(perk.partner_name)
                    ? perk.partner_name
                    : 'Yummy Bros'}
                </strong>
              </span>
            ) : null}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};
